import { useMemo, useState } from 'react';
import {
  KeyIcon,
  UsersThreeIcon,
  CheckCircleIcon,
  ArchiveIcon,
  ClockCounterClockwiseIcon,
  PencilSimpleIcon,
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { SearchBar } from '@/components/ui/search-bar';
import { Skeleton } from '@/components/ui/skeleton';
import { ToastNotification } from '@/components/ui/ToastNotification';
import { GroupFormDrawer } from '@/components/admin/GroupFormDrawer';
import { ArchiveGroupDrawer } from '@/components/admin/ArchiveGroupDrawer';
import { InitialsBadge } from '@/components/InitialsBadge';
import { ManageGroupsContent } from '@/content/ManageGroupsContent';
import {
  useGroup,
  useUpdateGroup,
  useArchiveGroup,
  useRestoreGroup,
} from '@/lib/hooks/useGroups';
import { useGroupMembers } from '@/lib/hooks/useMemberships';
import { useToastNotification } from '@/lib/hooks/useToastNotification';
import { useRoleDirectory } from '@/lib/hooks/useRoleDirectory';
import { getGroupRoleTone, getGroupStatus } from '@/lib/utils/groups.utils';
import { getRoleLabelByKey } from '@/lib/utils/roles.utils';
import { cn } from '@/lib/utils';
import type { GroupMemberRow, UserRole } from '@/lib/types/database';

interface GroupDetailProps {
  groupId: string;
}

export function GroupDetail({ groupId }: GroupDetailProps) {
  useRoleDirectory();
  const { data: group, isLoading, error } = useGroup(groupId);
  const {
    data: members = [],
    isLoading: isLoadingMembers,
    error: membersError,
  } = useGroupMembers(groupId);
  const updateGroupMutation = useUpdateGroup();
  const archiveGroupMutation = useArchiveGroup();
  const restoreGroupMutation = useRestoreGroup();
  const { toast, showToast, hideToast } = useToastNotification();

  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isArchiveOpen, setIsArchiveOpen] = useState(false);
  const [archiveMode, setArchiveMode] = useState<'archive' | 'restore'>(
    'archive'
  );
  const [memberSearch, setMemberSearch] = useState('');

  const filteredMembers = useMemo(() => {
    const query = memberSearch.trim().toLowerCase();
    if (!query) return members;
    return members.filter((m) => {
      const name = (m.full_name ?? '').toLowerCase();
      const email = (m.email ?? '').toLowerCase();
      return name.includes(query) || email.includes(query);
    });
  }, [members, memberSearch]);

  const handleEditSubmit = async (input: {
    displayName: string;
    parentRole: UserRole;
    description: string | null;
  }) => {
    if (!group) return;
    try {
      const saved = await updateGroupMutation.mutateAsync({
        groupId: group.id,
        ...input,
      });
      setIsEditOpen(false);
      showToast({
        title: ManageGroupsContent.toast.updateSuccessTitle,
        description: ManageGroupsContent.toast.updateSuccessDescription(
          saved.display_name
        ),
        variant: 'success',
      });
    } catch (err) {
      showToast({
        title: ManageGroupsContent.toast.errorTitle,
        description:
          err instanceof Error ? err.message : 'Failed to save group.',
        variant: 'error',
      });
    }
  };

  const openArchive = () => {
    setArchiveMode('archive');
    setIsArchiveOpen(true);
  };

  const openRestore = () => {
    setArchiveMode('restore');
    setIsArchiveOpen(true);
  };

  const handleArchiveConfirm = async () => {
    if (!group) return;
    try {
      if (archiveMode === 'archive') {
        const saved = await archiveGroupMutation.mutateAsync(group.id);
        setIsArchiveOpen(false);
        showToast({
          title: ManageGroupsContent.toast.archiveSuccessTitle,
          description: ManageGroupsContent.toast.archiveSuccessDescription(
            saved.display_name
          ),
          variant: 'success',
        });
      } else {
        const saved = await restoreGroupMutation.mutateAsync(group.id);
        setIsArchiveOpen(false);
        showToast({
          title: ManageGroupsContent.toast.restoreSuccessTitle,
          description: ManageGroupsContent.toast.restoreSuccessDescription(
            saved.display_name
          ),
          variant: 'success',
        });
      }
    } catch (err) {
      showToast({
        title: ManageGroupsContent.toast.errorTitle,
        description: err instanceof Error ? err.message : 'Action failed.',
        variant: 'error',
      });
    }
  };

  const isArchiveSubmitting =
    archiveGroupMutation.isPending || restoreGroupMutation.isPending;

  if (isLoading) {
    return (
      <div className="bg-slate-700">
        <div className="mx-auto flex w-full max-w-4xl flex-col px-4 pb-8 pt-3">
          <div className="space-y-4">
            <Skeleton className="h-8 w-48 rounded-full bg-slate-600" />
            <Skeleton className="h-24 w-full rounded-lg bg-slate-600" />
            <Skeleton className="h-20 w-full rounded-lg bg-slate-600" />
          </div>
        </div>
      </div>
    );
  }

  if (error || !group) {
    return (
      <div className="bg-slate-700">
        <div className="mx-auto flex w-full max-w-4xl flex-col px-4 pb-8 pt-3">
          <div className="rounded-lg border border-red-400 bg-red-500/10 px-4 py-6 text-center text-body text-red-100">
            {error instanceof Error ? error.message : 'Group not found.'}
          </div>
        </div>
      </div>
    );
  }

  const isArchived = !!group.archived_at;
  const groupStatus = getGroupStatus(group.archived_at ?? null);
  const tone = getGroupRoleTone(group.parent_role as UserRole);
  const content = ManageGroupsContent.detail;

  return (
    <div className="bg-slate-700">
      <div className="mx-auto flex w-full max-w-4xl flex-col px-4 pb-8 pt-3">
        {/* Header */}
        <div className="mb-5">
          <div className="mb-3 flex items-center gap-2">
            <RolePill parentRole={group.parent_role as UserRole} tone={tone} />
            {isArchived && (
              <span className="inline-flex items-center rounded-full border border-amber-400/40 bg-amber-400/15 px-2 py-0.5 font-field-42 text-[10px] uppercase text-amber-300">
                {content.archivedBadge}
              </span>
            )}
          </div>
          <h1
            className={cn(
              'font-field-74 text-h1 leading-tight',
              isArchived ? 'text-slate-400' : 'text-slate-50'
            )}
          >
            {group.display_name}
          </h1>
          {group.description && (
            <p className="mt-2 max-w-xl text-body text-slate-400">
              {group.description}
            </p>
          )}
        </div>

        {/* Import key strip */}
        <div className="mb-4 flex items-center gap-2 rounded-lg border border-slate-600 bg-slate-600/50 px-3 py-2.5">
          <KeyIcon
            size={14}
            weight="bold"
            className="shrink-0 text-slate-500"
          />
          <span className="font-field-42 text-[11px] uppercase text-slate-500">
            {content.importKeyLabel}
          </span>
          <code className="min-w-0 flex-1 truncate font-mono text-[13px] text-purple-300">
            {group.import_key}
          </code>
        </div>

        {/* Stat tiles */}
        <div className="mb-4 grid grid-cols-2 gap-3">
          <StatTile
            label={content.membersLabel}
            value={String(group.member_count)}
            icon={
              <UsersThreeIcon
                size={18}
                weight="duotone"
                className="text-slate-400"
              />
            }
          />
          <StatTile
            label={content.statusLabel}
            value={
              groupStatus === 'archived'
                ? content.archivedBadge
                : content.activeBadge
            }
            icon={
              groupStatus === 'archived' ? (
                <ArchiveIcon
                  size={18}
                  weight="duotone"
                  className="text-amber-400"
                />
              ) : (
                <CheckCircleIcon
                  size={18}
                  weight="duotone"
                  className="text-green-400"
                />
              )
            }
          />
        </div>

        {/* Action row */}
        <div className="mb-6 flex flex-wrap gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsEditOpen(true)}
            className="border-slate-550 bg-transparent text-slate-50 hover:bg-slate-600"
          >
            <PencilSimpleIcon size={16} weight="bold" />
            {ManageGroupsContent.actions.edit}
          </Button>
          {isArchived ? (
            <Button
              variant="outline"
              size="sm"
              onClick={openRestore}
              className="border-slate-550 bg-transparent text-slate-50 hover:bg-slate-600"
            >
              <ClockCounterClockwiseIcon size={16} weight="bold" />
              {ManageGroupsContent.actions.restore}
            </Button>
          ) : (
            <Button
              variant="outline"
              size="sm"
              onClick={openArchive}
              className="border-red-500/40 bg-transparent text-red-400 hover:bg-red-500/10"
            >
              <ArchiveIcon size={16} weight="bold" />
              {ManageGroupsContent.actions.archive}
            </Button>
          )}
        </div>

        {/* Member list */}
        <div className="rounded-lg border border-slate-600 bg-slate-600">
          <div className="border-b border-slate-550 px-4 py-3">
            <h2 className="text-large text-slate-50">
              {content.membersSection(members.length)}
            </h2>
          </div>
          <div className="px-4 py-3">
            <div className="mb-3">
              <SearchBar
                value={memberSearch}
                onChange={setMemberSearch}
                placeholder={content.memberListSearchPlaceholder}
                className="[&_input]:border-slate-500 [&_input]:bg-slate-700 [&_input]:text-slate-50 [&_input]:placeholder:text-slate-500"
              />
            </div>
            {isLoadingMembers ? (
              <div className="space-y-2">
                {[...Array(3)].map((_, i) => (
                  <Skeleton
                    key={i}
                    className="h-14 w-full rounded-lg bg-slate-700"
                  />
                ))}
              </div>
            ) : membersError ? (
              <div className="rounded-lg border border-red-400 bg-red-500/10 px-3 py-3 text-body text-red-100">
                {membersError instanceof Error
                  ? membersError.message
                  : content.memberListLoadError}
              </div>
            ) : filteredMembers.length === 0 ? (
              <div className="py-8 text-center">
                <UsersThreeIcon
                  size={28}
                  weight="duotone"
                  className="mx-auto mb-2 text-slate-500"
                />
                <p className="text-body text-slate-500">
                  {memberSearch.trim()
                    ? content.memberListSearchEmpty
                    : content.memberListEmptyState}
                </p>
              </div>
            ) : (
              <ul className="divide-y divide-slate-550/40">
                {filteredMembers.map((member) => (
                  <MemberRow key={member.user_id} member={member} />
                ))}
              </ul>
            )}
          </div>
          <div className="border-t border-slate-550 px-4 py-3">
            <p className="text-small text-slate-500">
              {content.membershipFooter}
            </p>
          </div>
        </div>
      </div>

      <GroupFormDrawer
        open={isEditOpen}
        onOpenChange={(open) => {
          setIsEditOpen(open);
        }}
        group={group}
        isSubmitting={updateGroupMutation.isPending}
        onSubmit={handleEditSubmit}
        onRequestArchive={openArchive}
        onRequestRestore={openRestore}
      />

      <ArchiveGroupDrawer
        open={isArchiveOpen}
        onOpenChange={setIsArchiveOpen}
        mode={archiveMode}
        groupName={group.display_name}
        isSubmitting={isArchiveSubmitting}
        onConfirm={handleArchiveConfirm}
      />

      <ToastNotification
        open={toast.open}
        onClose={hideToast}
        title={toast.title}
        description={toast.description}
        variant={toast.variant}
        position="bottom"
      />
    </div>
  );
}

interface StatTileProps {
  label: string;
  value: string;
  icon: React.ReactNode;
}

function StatTile({ label, value, icon }: StatTileProps) {
  return (
    <div className="flex items-center gap-3 rounded-lg border border-slate-600 bg-slate-600/50 px-4 py-3">
      {icon}
      <div>
        <div className="font-field-64 text-[20px] leading-5 text-slate-50">
          {value}
        </div>
        <div className="mt-0.5 text-[11px] text-slate-500">{label}</div>
      </div>
    </div>
  );
}

interface MemberRowProps {
  member: GroupMemberRow;
}

function MemberRow({ member }: MemberRowProps) {
  const displayName = member.full_name || member.email || 'Unknown member';
  const tone = member.role ? getGroupRoleTone(member.role) : null;
  const assignedDate = new Date(member.assigned_at).toLocaleDateString(
    'en-US',
    { month: 'short', day: 'numeric', year: 'numeric' }
  );
  return (
    <li className="flex items-center gap-3 py-2.5">
      <InitialsBadge name={displayName} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="truncate text-body text-slate-50">
            {displayName}
          </span>
          {member.role && tone && (
            <span
              className={cn(
                'inline-flex shrink-0 items-center rounded-full border px-2 py-0.5 font-field-42 text-[9px] uppercase',
                tone.pillClassName
              )}
            >
              {getRoleLabelByKey(member.role)}
            </span>
          )}
        </div>
        {member.email && (
          <p className="truncate text-small text-slate-500">{member.email}</p>
        )}
      </div>
      <span className="shrink-0 text-small text-slate-500">
        {ManageGroupsContent.detail.memberAssignedOn(assignedDate)}
      </span>
    </li>
  );
}

interface RolePillProps {
  parentRole: UserRole;
  tone: { pillClassName: string };
}

function RolePill({ parentRole, tone }: RolePillProps) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 font-field-42 text-[10px] uppercase text-slate-300',
        tone.pillClassName
      )}
    >
      <span className="h-1 w-1 rounded-full bg-current" />
      {getRoleLabelByKey(parentRole)}
    </span>
  );
}
